function text(value) {
  return String(value == null ? '' : value).trim();
}

export function slugifyRouteName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u064b-\u065f\u0670]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\u0600-\u06ff]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'zgharta';
}

export const MAX_SLUG_LENGTH = 80;

// Callers slugify this result again, so the base gives up room to preserve the suffix.
export function composeDisambiguatedSlug(baseSlug, suffix) {
  const base = text(baseSlug);
  const tail = text(suffix);
  if (!tail) return base;
  const room = MAX_SLUG_LENGTH - tail.length - 1;
  if (room <= 0) return tail.slice(0, MAX_SLUG_LENGTH).replace(/-+$/, '');
  const head = base.slice(0, room).replace(/-+$/, '');
  return head ? `${head}-${tail}` : tail;
}

export function routeIdOf(item) {
  return item?.routeId ?? item?.id;
}

function groupIndexes(values) {
  return values.reduce((groups, value, index) => {
    const members = groups[value] || [];
    members.push(index);
    groups[value] = members;
    return groups;
  }, Object.create(null));
}

export function assignRouteSlugs(items = []) {
  const list = Array.isArray(items) ? items : [];
  const baseSlugs = list.map(item => slugifyRouteName(item?.name));
  const baseGroups = groupIndexes(baseSlugs);
  const baseEntries = Object.entries(baseGroups);

  const reservedSlugs = new Set(baseEntries
    .filter(([, members]) => members.length === 1)
    .map(([slug]) => slug));
  const collidingGroups = baseEntries.filter(([, members]) => members.length > 1);

  const slugs = baseSlugs.slice();

  for (const [baseSlug, members] of collidingGroups) {
    const byLocality = members.map(index => {
      const item = list[index];
      const locality = text(item?.village) || text(item?.location);
      return locality ? composeDisambiguatedSlug(baseSlug, slugifyRouteName(locality)) : baseSlug;
    });
    const localityGroups = groupIndexes(byLocality);

    members.forEach((index, position) => {
      const candidate = byLocality[position];
      // Locality works only when unique inside the group and outside reserved routes.
      const usable = candidate !== baseSlug
        && localityGroups[candidate].length === 1
        && !reservedSlugs.has(candidate);
      // Missing ids keep the candidate so the final duplicate audit reports the drop.
      const id = text(routeIdOf(list[index]));
      slugs[index] = usable || !id ? candidate : composeDisambiguatedSlug(candidate, slugifyRouteName(id));
    });
  }

  const finalGroups = groupIndexes(slugs);
  const droppedSlugs = Object.keys(finalGroups)
    .filter(slug => finalGroups[slug].length > 1)
    .sort();
  const dropped = droppedSlugs.reduce((total, slug) => total + finalGroups[slug].length - 1, 0);

  return {
    slugs,
    summary: {
      total: list.length,
      collidingGroups: collidingGroups.length,
      disambiguated: collidingGroups.reduce((total, [, members]) => total + members.length, 0),
      dropped,
      droppedSlugs,
    },
  };
}

export function formatSlugCollisionSummary(summaries) {
  const entries = Object.entries(summaries || {});
  const groups = entries.reduce((total, [, s]) => total + (s?.collidingGroups || 0), 0);
  const disambiguated = entries.reduce((total, [, s]) => total + (s?.disambiguated || 0), 0);
  const dropped = entries.reduce((total, [, s]) => total + (s?.dropped || 0), 0);
  const droppedDetail = entries
    .flatMap(([kind, s]) => (s?.droppedSlugs || []).map(slug => `${kind}/${slug}`))
    .join(', ');

  return `slug collisions: ${groups} name group(s), ${disambiguated} page(s) disambiguated, `
    + `${dropped} page(s) dropped${droppedDetail ? ` (${droppedDetail})` : ''}`;
}
